import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import ReactGA from 'react-ga4';
import { BUILD_METADATA } from '../utils/buildMetadata';
import { getAnalyticsConsent, saveAnalyticsConsent } from './analyticsConsent';
import { getPageAnalyticsContext } from './analyticsContext';

// Analytics configuration interface
export interface AnalyticsConfig {
  googleAnalytics?: {
    measurementId: string;
  };
  applicationInsights?: {
    connectionString: string;
  };
  clarity?: {
    projectId: string;
  };
}

// Custom event interface
export interface AnalyticsEvent {
  action: string;
  category: string;
  label?: string;
  value?: number;
  custom_parameters?: Record<string, any>;
}

export interface ApplicationInsightsEvent {
  name: string;
  properties?: Record<string, string | number | boolean | null | undefined>;
  measurements?: Record<string, number | null | undefined>;
}

// Page view interface
export interface PageView {
  page_title: string;
  page_location: string;
  page_path: string;
  custom_parameters?: Record<string, any>;
}

const CLARITY_UPGRADE_EVENTS = new Set([
  'event_detail_registration_click',
  'event_group_create_success',
  'event_group_create_reused',
  'event_group_create_error',
  'event_group_share_link',
  'error_exception',
]);

const CLARITY_DIMENSIONS = new Set([
  'action_location',
  'destination',
  'entity_type',
  'event_type',
  'filter_group',
  'format',
  'outcome',
  'page_type',
  'provider',
  'relationship',
  'route_template',
  'scope',
  'search_mode',
  'source',
  'view',
]);

const CLARITY_TAG_URL = 'https://www.clarity.ms/tag/';
const CLARITY_TAG_RETRY_DELAYS_MS = [5000];

export function shouldEnableExternalAnalytics(
  nodeEnvironment = process.env.NODE_ENV,
  developmentOverride = process.env.REACT_APP_ENABLE_DEV_ANALYTICS,
  deploymentEnvironment = process.env.REACT_APP_DEPLOYMENT_ENVIRONMENT,
): boolean {
  const explicitEnvironment = deploymentEnvironment?.trim().toLowerCase();
  if (explicitEnvironment) return explicitEnvironment === 'production';
  return nodeEnvironment !== 'development' || developmentOverride === 'true';
}

function eventName(category: string, action: string): string {
  return `${category}_${action}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80);
}

function cleanProperties(properties: Record<string, any> = {}): Record<string, string | number | boolean> {
  return Object.fromEntries(
    Object.entries(properties)
      .filter(([, value]) => value !== undefined && value !== null)
      .map(([key, value]) => [key, typeof value === 'object' ? JSON.stringify(value) : value]),
  );
}

class AnalyticsService {
  private appInsights: ApplicationInsights | null = null;
  private isGAInitialized = false;
  private isClarityInitialized = false;
  private config: AnalyticsConfig = {};
  private pendingGooglePageView: PageView | null = null;
  private externalAnalyticsEnabled = shouldEnableExternalAnalytics();

  /**
   * Initialize all analytics services based on provided configuration
   */
  public async initialize(config: AnalyticsConfig): Promise<void> {
    this.config = config;
    const hasAnalyticsConsent = getAnalyticsConsent() === 'granted';

    // Log development mode status
    if (process.env.NODE_ENV === 'development' && !this.externalAnalyticsEnabled) {
      console.log('🔧 Analytics running in DEVELOPMENT mode - external services disabled');
    }
    if (!this.externalAnalyticsEnabled) return;

    // Initialize Google Analytics
    if (config.googleAnalytics?.measurementId && hasAnalyticsConsent) {
      this.applyGoogleConsent(config.googleAnalytics.measurementId, true, 'default');
      await this.initializeGoogleAnalytics(config.googleAnalytics.measurementId);
    }

    // Initialize Azure Application Insights
    if (config.applicationInsights?.connectionString) {
      this.initializeApplicationInsights(config.applicationInsights.connectionString, hasAnalyticsConsent);
    }

    // Initialize Microsoft Clarity
    if (config.clarity?.projectId) {
      this.initializeClarity(config.clarity.projectId);
      this.applyClarityConsent(hasAnalyticsConsent);
    }
  }

  /**
   * Initialize Google Analytics 4
   */
  private async initializeGoogleAnalytics(measurementId: string): Promise<void> {
    try {


      // Initialize ReactGA
      ReactGA.initialize(measurementId, {
        testMode: process.env.NODE_ENV === 'test',
        gtagOptions: {
          debug_mode: false, // Disable debug mode to reduce console noise
          send_page_view: false, // We'll handle page views manually
        },
      });

      this.isGAInitialized = true;
      this.flushGooglePageView();
      console.log('Google Analytics initialized with ID:', measurementId);
    } catch (error) {
      console.error('Failed to initialize Google Analytics:', error);
      this.isGAInitialized = false;
    }
  }

  private applyGoogleConsent(
    measurementId: string,
    granted: boolean,
    command: 'default' | 'update',
  ): void {
    const analyticsWindow = window as any;
    analyticsWindow.dataLayer = analyticsWindow.dataLayer || [];
    analyticsWindow.gtag = analyticsWindow.gtag || function gtag() {
      analyticsWindow.dataLayer.push(arguments);
    };
    analyticsWindow[`ga-disable-${measurementId}`] = !granted;
    analyticsWindow.gtag('consent', command, {
      analytics_storage: granted ? 'granted' : 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });
  }

  /**
   * Initialize Azure Application Insights
   */
  private initializeApplicationInsights(connectionString: string, cookiesEnabled: boolean): void {
    try {
      // Disable Azure Application Insights in development environment
      this.appInsights = new ApplicationInsights({
        config: {
          connectionString,
          enableAutoRouteTracking: false,
          enableCorsCorrelation: true,
          enableRequestHeaderTracking: true,
          enableResponseHeaderTracking: true,
          enableAjaxErrorStatusText: true,
          enableAjaxPerfTracking: true,
          enableUnhandledPromiseRejectionTracking: true,
          disablePageUnloadEvents: ['unload'],
          disableCookiesUsage: !cookiesEnabled,
        },
      });

      this.appInsights.loadAppInsights();

      console.log('Azure Application Insights initialized');
    } catch (error) {
      console.error('Failed to initialize Azure Application Insights:', error);
    }
  }

  /**
   * Initialize Microsoft Clarity
   */
  private initializeClarity(projectId: string): void {
    const analyticsWindow = window as any;
    analyticsWindow.clarity = analyticsWindow.clarity || function clarityCommand(...args: any[]) {
      analyticsWindow.clarity.q = analyticsWindow.clarity.q || [];
      analyticsWindow.clarity.q.push(args);
    };
    analyticsWindow.clarity.eventFinderStub = true;
    this.isClarityInitialized = false;

    const loadTag = (attempt: number): void => {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = `${CLARITY_TAG_URL}${encodeURIComponent(projectId)}`;
      script.dataset.clarityLoader = 'eventfinder';
      script.onload = () => {
        this.isClarityInitialized = true;
        console.log('Microsoft Clarity initialized with project ID:', projectId);
        if (attempt > 0) {
          this.trackApplicationInsightsEvent({
            name: 'ClarityInitializationRecovered',
            properties: { attempts: attempt + 1 },
          });
        }
      };
      script.onerror = () => {
        script.remove();
        const retryDelay = CLARITY_TAG_RETRY_DELAYS_MS[attempt];
        if (retryDelay !== undefined) {
          window.setTimeout(() => loadTag(attempt + 1), retryDelay);
          return;
        }
        this.isClarityInitialized = false;
        if (analyticsWindow.clarity?.eventFinderStub) delete analyticsWindow.clarity;
        this.trackApplicationInsightsEvent({
          name: 'ClarityInitializationFailed',
          properties: { attempts: attempt + 1 },
        });
      };
      document.head.appendChild(script);

    };

    loadTag(0);
  }

  private applyClarityConsent(granted: boolean): void {
    if (!(window as any).clarity) return;
    (window as any).clarity('consentv2', {
      ad_Storage: 'denied',
      analytics_Storage: granted ? 'granted' : 'denied',
    });
  }

  public async setAnalyticsConsent(granted: boolean): Promise<void> {
    saveAnalyticsConsent(granted ? 'granted' : 'denied');
    this.applyClarityConsent(granted);
    this.appInsights?.getCookieMgr().setEnabled(granted);

    const measurementId = this.config.googleAnalytics?.measurementId;
    if (measurementId && this.externalAnalyticsEnabled) {
      this.applyGoogleConsent(measurementId, granted, this.isGAInitialized ? 'update' : 'default');
      if (granted && !this.isGAInitialized) await this.initializeGoogleAnalytics(measurementId);
      if (granted) this.flushGooglePageView();
    }
    this.trackApplicationInsightsEvent({
      name: 'AnalyticsConsentChanged',
      properties: { analyticsConsent: granted ? 'granted' : 'denied' },
    });
  }

  public identifyAuthenticatedUser(userId: string, role: string, pageId: string): void {
    if (!userId || getAnalyticsConsent() !== 'granted') return;
    this.appInsights?.setAuthenticatedUserContext(userId, undefined, true);
    if (this.isGAInitialized) ReactGA.set({ user_id: userId, user_role: role });
    if ((window as any).clarity) {
      (window as any).clarity('identify', userId, undefined, pageId, role);
      (window as any).clarity('set', 'user_role', role);
    }
  }

  private flushGooglePageView(): void {
    if (!this.isGAInitialized || getAnalyticsConsent() !== 'granted' || !this.pendingGooglePageView) return;
    const { page_title, page_location, page_path, custom_parameters } = this.pendingGooglePageView;
    ReactGA.send({
      hitType: 'pageview',
      page: page_path,
      title: page_title,
      location: page_location,
      ...cleanProperties({ ...custom_parameters, app_release: BUILD_METADATA.commitHashShort }),
    });
    this.pendingGooglePageView = null;
  }

  /**
   * Track page view across all analytics services
   */
  public trackPageView(pageView: PageView): void {
    const { page_title, page_location, custom_parameters } = pageView;
    const properties = cleanProperties({
      ...custom_parameters,
      app_release: BUILD_METADATA.commitHashShort,
    });

    // Google Analytics
    this.pendingGooglePageView = pageView;
    this.flushGooglePageView();

    // Azure Application Insights
    if (this.appInsights) {
      this.appInsights.trackPageView({
        name: page_title,
        uri: page_location,
        properties,
      });
    }

    // Microsoft Clarity (automatically tracks page views)
    if ((window as any).clarity) {
      Object.entries(properties).forEach(([key, value]) => {
        if (CLARITY_DIMENSIONS.has(key) || key === 'app_release') {
          (window as any).clarity('set', key, String(value).slice(0, 120));
        }
      });
    }
  }

  /**
   * Track custom events across all analytics services
   */
  public trackEvent(event: AnalyticsEvent): void {
    const { action, category, label, value, custom_parameters } = event;
    const name = eventName(category, action);
    const pageContext = typeof window === 'undefined'
      ? { page_type: 'unknown', route_template: 'unknown' }
      : getPageAnalyticsContext(window.location.pathname, window.location.search);
    const properties = cleanProperties({
      category,
      action,
      label,
      value,
      ...custom_parameters,
      ...pageContext,
      page_path: typeof window === 'undefined' ? undefined : window.location.pathname,
      page_title: typeof document === 'undefined' ? undefined : document.title,
      app_release: BUILD_METADATA.commitHashShort,
    });

    // Google Analytics
    if (this.isGAInitialized) {
      ReactGA.event(name, {
        event_category: category,
        event_action: action,
        event_label: label,
        ...properties,
      });
    }

    // Azure Application Insights
    if (this.appInsights) {
      this.appInsights.trackEvent({
        name,
        properties,
      });
    }

    // Microsoft Clarity
    if ((window as any).clarity) {
      (window as any).clarity('set', 'event_category', category.slice(0, 80));
      (window as any).clarity('set', 'event_action', action.slice(0, 80));
      Object.entries(properties).forEach(([key, propertyValue]) => {
        if (CLARITY_DIMENSIONS.has(key)) {
          (window as any).clarity('set', key, String(propertyValue).slice(0, 120));
        }
      });
      (window as any).clarity('event', name);
      if (CLARITY_UPGRADE_EVENTS.has(name)) (window as any).clarity('upgrade', name);
    }
  }

  public trackApplicationInsightsEvent(event: ApplicationInsightsEvent): void {
    if (!this.appInsights) return;
    try {
      const properties = Object.fromEntries(
        Object.entries(event.properties || {})
          .filter(([, value]) => value !== null && value !== undefined)
          .map(([key, value]) => [key, String(value).slice(0, 500)]),
      );
      const measurements = Object.fromEntries(
        Object.entries(event.measurements || {})
          .filter(([, value]) => typeof value === 'number' && Number.isFinite(value)),
      ) as Record<string, number>;
      this.appInsights.trackEvent({ name: event.name, properties, measurements });
    } catch {
      // Product workflows must not depend on telemetry availability.
    }
  }

  /**
   * Track user interactions (clicks, form submissions, etc.)
   */
  public trackInteraction(element: string, action: string, properties?: Record<string, any>): void {
    this.trackEvent({
      action,
      category: 'user_interaction',
      label: element,
      custom_parameters: properties,
    });
  }

  /**
   * Track search events
   */
  public trackSearch(searchTerm: string, resultsCount?: number, filters?: Record<string, any>): void {
    this.trackEvent({
      action: 'search',
      category: 'search',
      label: searchTerm,
      value: resultsCount,
      custom_parameters: {
        search_term: searchTerm,
        results_count: resultsCount,
        filters,
      },
    });
  }

  /**
   * Track event views
   */
  public trackEventView(eventId: string, eventTitle: string, properties?: Record<string, any>): void {
    this.trackEvent({
      action: 'view_event',
      category: 'events',
      label: eventTitle,
      custom_parameters: {
        event_id: eventId,
        event_title: eventTitle,
        ...properties,
      },
    });
  }

  /**
   * Track errors
   */
  public trackError(error: Error, context?: string): void {
    // Azure Application Insights
    if (this.appInsights) {
      this.appInsights.trackException({
        exception: error,
        properties: {
          context,
        },
      });
    }

    // Google Analytics
    if (this.isGAInitialized) {
      ReactGA.event({
        action: 'exception',
        category: 'error',
        label: error.message,
        custom_error_name: error.name,
        custom_context: context,
      } as any);
    }

    if ((window as any).clarity) {
      if (context) (window as any).clarity('set', 'error_context', context.slice(0, 120));
      (window as any).clarity('event', 'error_exception');
      (window as any).clarity('upgrade', 'error_exception');
    }
  }

  /**
   * Set user properties
   */
  public setUserProperties(properties: Record<string, any>): void {
    // Google Analytics
    if (this.isGAInitialized) {
      ReactGA.set(properties);
    }

    // Azure Application Insights
    if (this.appInsights) {
      this.appInsights.setAuthenticatedUserContext(
        properties.user_id,
        properties.account_id,
        true
      );
    }

    // Clarity custom tags stay low-cardinality. Stable user identity belongs
    // in the hashed Identify API, not in a readable custom tag.
    if ((window as any).clarity) {
      Object.entries(properties).forEach(([key, value]) => {
        if (CLARITY_DIMENSIONS.has(key) || key === 'user_role') {
          (window as any).clarity('set', key, String(value).slice(0, 120));
        }
      });
    }
  }

  /**
   * Get the analytics configuration
   */
  public getConfig(): AnalyticsConfig {
    return this.config;
  }

  /**
   * Check if analytics services are initialized
   */
  public getStatus(): {
    googleAnalytics: boolean;
    applicationInsights: boolean;
    clarity: boolean;
  } {
    return {
      googleAnalytics: this.isGAInitialized,
      applicationInsights: this.appInsights !== null,
      clarity: this.isClarityInitialized,
    };
  }
}

// Create and export a singleton instance
export const analytics = new AnalyticsService();

// Export the service class for testing
export default AnalyticsService;